import {
  EditContactForm,
  type EditContactFormHandle,
} from "@/components/features/contact/AddOrEditContactModal/EditContactForm";
import { AppModal, Button, Form, LoadingSpinner } from "@/components/ui";
import { AddContactFormField } from "@/components/ui/FormFields/AddContactFormField/AddContactFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { MatterContactConnectionModalPreview } from "@/components/ui/MatterContactConnectionRichCard/MatterContactConnectionModalPreview";
import { useUser } from "@/components/UserProvider";
import type { TreatmentViewModel } from "@/hooks/matter-view-models/use-medical-treatment-view-model";
import { retrieveContactQueryOptions } from "@/lib/data/react-query";
import { useSyncMedicalTreatmentContactConnections } from "@/lib/data/react-query/connections/medical-treatment-contact-connection";
import { useUpdateMedicalTreatment } from "@/lib/data/react-query/matters/medical-treatment";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Connection, Contact, MatterContactConnection } from "@/lib/types";
import { getConnectionPreviewContact } from "@/lib/utils/connections/item-contact-connections";
import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
  MedicalTreatmentDetailsFields,
  type MedicalTreatmentDetailsDraft,
} from "./MedicalTreatmentDetailsFields";

const getTreatmentDraft = (
  treatment: TreatmentViewModel,
): MedicalTreatmentDetailsDraft => ({
  type: treatment.type,
  status: treatment.status ?? "",
  patientAccountNumber: treatment.patientAccountNumber ?? "",
  portalLink: treatment.portalLink ?? "",
  portalDetails: treatment.portalDetails ?? "",
  description: treatment.description ?? "",
  letterOfProtection: treatment.letterOfProtection ?? "",
  lien: treatment.lien ?? "",
  billNotApplicable: treatment.billNotApplicable ?? false,
  recordNotApplicable: treatment.recordNotApplicable ?? false,
  planNotApplicable: treatment.planNotApplicable ?? false,
});

const getInitialContactSelection = (
  connection: Connection | undefined,
  type: Contact["type"],
): Contact | null => {
  const preview = getConnectionPreviewContact(connection);
  if (preview) return preview as Contact;
  if (!connection) return null;
  // Missing preview data must not clear an existing connection on Save.
  return {
    id: connection.targetObjectId,
    type,
    contactName: "Unknown contact",
  } as unknown as Contact;
};

export function MedicalTreatmentDetailsModal({
  treatment,
  matterId,
  setOpen,
  onDeleteTreatment,
}: {
  treatment: TreatmentViewModel;
  matterId: string;
  setOpen: (open: boolean) => void;
  onDeleteTreatment?: () => void;
}) {
  const { hasActionPermission } = useUser();
  const canUpdateTreatment = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterMedicalTreatment,
    ACTION_PERMISSION_VALUES.update,
  );
  const canEditContact = hasActionPermission(
    ACTION_PERMISSION_KEYS.contact,
    ACTION_PERMISSION_VALUES.update,
  );
  const [draft, setDraft] = useState(() => getTreatmentDraft(treatment));
  const [contactDirty, setContactDirty] = useState(false);
  const [contactPending, setContactPending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const contactFormRef = useRef<EditContactFormHandle>(null);
  const pickerForm = useForm<{
    provider: Contact | null;
    treatingPhysician: Contact | null;
  }>({
    defaultValues: {
      provider: getInitialContactSelection(treatment.provider, "organization"),
      treatingPhysician: getInitialContactSelection(
        treatment.treatingPhysician,
        "individual",
      ),
    },
  });
  const selectedProvider = pickerForm.watch("provider");
  const selectedPhysician = pickerForm.watch("treatingPhysician");
  const { data: providerData, isPending: providerPending } = useQuery({
    ...retrieveContactQueryOptions(selectedProvider?.id),
    enabled: Boolean(selectedProvider?.id),
  });
  const updateTreatment = useUpdateMedicalTreatment();
  const syncConnections = useSyncMedicalTreatmentContactConnections();
  const treatmentChanged =
    JSON.stringify(draft) !== JSON.stringify(getTreatmentDraft(treatment));
  const providerChanged =
    (selectedProvider?.id ?? "") !== (treatment.provider?.targetObjectId ?? "");
  const physicianChanged =
    (selectedPhysician?.id ?? "") !==
    (treatment.treatingPhysician?.targetObjectId ?? "");
  const dirty =
    treatmentChanged || providerChanged || physicianChanged || contactDirty;
  const pending =
    submitting ||
    contactPending ||
    updateTreatment.isPending ||
    syncConnections.isPending ||
    (Boolean(selectedProvider?.id) && providerPending);
  const clearProvider = () => {
    pickerForm.setValue("provider", null, { shouldDirty: true });
    setContactDirty(false);
    setContactPending(false);
  };
  const providerConnection =
    treatment.provider?.targetObjectId === selectedProvider?.id
      ? treatment.provider
      : { label: "Provider", contactId: selectedProvider?.id };
  const physicianPicker = (
    <div className="mt-4">
      <Form {...pickerForm}>
        <AddContactFormField
          contactSelectorSize="compact"
          hideCompactContactChrome
          label="Treating physician"
          register={pickerForm.register("treatingPhysician")}
          disableSelectContactButton={!canUpdateTreatment}
          onDelete={() => {
            pickerForm.setValue("treatingPhysician", null, {
              shouldDirty: true,
            });
          }}
          matterId={matterId}
          defaultSearchObject={{ type: ["individual"] }}
          defaultSearchField="name"
          defaultValues={{
            type: "individual",
            individualSubTypes: ["Medical Professional"],
          }}
          renderSelectedContactPreview={(contact) => (
            <MatterContactConnectionModalPreview
              contactId={contact.id}
              previewContact={
                Array.isArray(contact.phones) ? contact : undefined
              }
              fetchContactOnEdit
              matterId={matterId}
              visualVariant="white"
              matterContactConnection={
                (treatment.treatingPhysician?.targetObjectId === contact.id
                  ? treatment.treatingPhysician
                  : {
                      label: "Treating physician",
                      contactId: contact.id,
                    }) as MatterContactConnection
              }
              onRemoveContact={
                canUpdateTreatment
                  ? () => {
                      pickerForm.setValue("treatingPhysician", null, {
                        shouldDirty: true,
                      });
                    }
                  : undefined
              }
            />
          )}
        />
      </Form>
    </div>
  );
  const treatmentFields = (
    <MedicalTreatmentDetailsFields
      values={draft}
      onChange={(updates) => {
        setDraft((current) => ({ ...current, ...updates }));
      }}
      disabled={!canUpdateTreatment}
      hasBills={treatment.bills.length > 0}
      hasRecords={treatment.records.length > 0}
      hasPlans={treatment.plans.length > 0}
    />
  );

  return (
    <AppModal
      open
      onOpenChange={setOpen}
      title="Edit treatment details"
      description="Update treatment details and provider information."
      size="3xl"
      className="h-[min(80vh,780px)] w-[calc(100vw-2rem)] max-w-5xl"
      bodyClassName="min-h-0 px-1"
      bodyContained
      isDirty={dirty}
      loading={pending}
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <div>
            {onDeleteTreatment && (
              <Button
                type="button"
                variant="outline"
                className="text-destructive border-destructive/30 hover:bg-destructive/5 hover:text-destructive"
                disabled={pending}
                onClick={onDeleteTreatment}
              >
                Delete treatment
              </Button>
            )}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <FormCancelButton setOpen={setOpen} isDirty={dirty} />
            <Button
              type="button"
              disabled={pending || !dirty || !canUpdateTreatment}
              onClick={async () => {
                setSubmitting(true);
                try {
                  if (contactDirty) {
                    const saved = await contactFormRef.current?.submit();
                    if (!saved) return;
                  }
                  if (treatmentChanged) {
                    await updateTreatment.mutateAsync({
                      ...draft,
                      matterId,
                      id: treatment.id,
                      dateModified: treatment.dateModified,
                    });
                  }
                  if (providerChanged || physicianChanged) {
                    await syncConnections.mutateAsync({
                      currentTreatment: treatment,
                      desiredContactIdsByRole: {
                        ...(providerChanged
                          ? { Provider: selectedProvider?.id }
                          : {}),
                        ...(physicianChanged
                          ? { "Treating Physician": selectedPhysician?.id }
                          : {}),
                      },
                      matterId,
                      treatmentId: treatment.id,
                    });
                  }
                  setOpen(false);
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {pending ? <LoadingSpinner /> : "Save"}
            </Button>
          </div>
        </div>
      }
    >
      {selectedProvider?.id ? (
        <EditContactForm
          key={selectedProvider.id}
          ref={contactFormRef}
          open
          setOpen={setOpen}
          activeContactId={selectedProvider.id}
          activeContact={providerData?.data}
          hideFooter
          closeOnSave={false}
          hasEditPermission={canEditContact}
          onDirtyChange={setContactDirty}
          onMutationPendingChange={setContactPending}
          livePreviewLayout
          matterContactConnection={
            providerConnection as MatterContactConnection
          }
          matterId={matterId}
          scrollContainer="self"
          onRemoveContact={canUpdateTreatment ? clearProvider : undefined}
          previewBottomContent={physicianPicker}
          topContent={
            <>
              {treatmentFields}
              <div className="border-border mx-3 mb-4 border-b pb-2">
                <h3 className="text-sm font-semibold">Edit contact info</h3>
              </div>
            </>
          }
        />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
          <aside className="border-border shrink-0 overflow-y-auto border-b px-5 pt-3 pb-4 lg:w-[380px] lg:border-r lg:border-b-0">
            <p className="text-muted-foreground mb-2 text-xs font-medium tracking-wide uppercase">
              Preview
            </p>
            <Form {...pickerForm}>
              <AddContactFormField
                label="Provider"
                register={pickerForm.register("provider")}
                disableSelectContactButton={!canUpdateTreatment}
                defaultSearchObject={{
                  type: ["organization"],
                  organizationSubTypes: ["Medical Provider"],
                }}
                defaultSearchField="name"
                defaultValues={{
                  type: "organization",
                  organizationSubTypes: ["Medical Provider"],
                }}
                createNewContactLabel="Create new provider"
                showEmbeddedCreateSuccessToast={false}
              />
            </Form>
            {physicianPicker}
          </aside>
          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            {treatmentFields}
          </div>
        </div>
      )}
    </AppModal>
  );
}
